(() => {
  'use strict';

  if (window.location.pathname.replace(/\/$/, '') !== '/search') return;
  if (window.PaulissenSkuSearchEngineReady) return;
  window.PaulissenSkuSearchEngineReady = true;

  const SEARCH_ROOT = '[data-pw-predictive-search]';
  const SEARCH_SECTION = '[data-search-results-section]';
  const MIN_QUERY = 2;
  const INDEX_MIN = 4;
  const RESULT_LIMIT = 24;
  const INDEX_CACHE_TTL = 30 * 60 * 1000;
  const INDEX_CACHE_VERSION = 'all-sku-v1';

  let runSerial = 0;
  let catalogPromise = null;

  const root = () => document.querySelector(SEARCH_ROOT);
  const section = () => document.querySelector(SEARCH_SECTION);
  const normalizeId = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  const isCompact = (value) => !/\s/.test(String(value || '').trim());

  const productUrl = (product) => product?.url || (product?.handle ? `/products/${product.handle}` : '');

  const productPath = (product) => {
    const value = productUrl(product);
    if (!value) return '';
    try {
      return new URL(value, window.location.origin).pathname.replace(/\/$/, '').toLowerCase();
    } catch (_) {
      return String(value).split('?')[0].replace(/\/$/, '').toLowerCase();
    }
  };

  const isHidden = (product) => {
    if (product?.hidden) return true;
    const tags = Array.isArray(product?.tags)
      ? product.tags.map((tag) => String(tag).trim().toLowerCase())
      : [];
    return tags.includes('eol') || tags.includes('product archief');
  };

  const queryCandidates = (query) => {
    const value = String(query || '').trim();
    const candidates = [value];
    const compact = value.replace(/[\s._\-/]+/g, '');
    if (compact && compact !== value) candidates.push(compact);

    if (/[a-z]/i.test(value) && /\d/.test(value)) {
      const split = value
        .replace(/([a-z])(\d)/gi, '$1 $2')
        .replace(/(\d)([a-z])/gi, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim();
      if (split && split !== value) candidates.push(split);
    }

    return [...new Set(candidates.filter((candidate) => candidate.length >= MIN_QUERY))].slice(0, 3);
  };

  const predictiveEndpoint = () => {
    const configured = root()?.dataset.predictiveSearchUrl || '/search/suggest';
    return configured.endsWith('.json') ? configured : `${configured}.json`;
  };

  const fetchPredictive = async (query) => {
    const url = new URL(predictiveEndpoint(), window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', String(Math.min(10, RESULT_LIMIT)));
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set(
      'resources[options][fields]',
      'title,vendor,product_type,tag,variants.title,variants.sku,variants.barcode'
    );

    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(`Predictive search failed (${response.status}).`);
    const payload = await response.json();
    return payload?.resources?.results?.products || [];
  };

  const requestIndex = async (url) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(`SKU-index failed (${response.status}).`);
    const payload = await response.json();
    if (!payload || !Array.isArray(payload.products)) throw new Error('SKU-index gaf ongeldige data terug.');
    return payload;
  };

  const fetchIndexPage = async (baseUrl, page) => {
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('page', String(page));
    url.searchParams.set('_pw_sku_refresh', `${Date.now()}-${page}`);

    const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);

    try {
      return await requestIndex(url);
    } catch (previewError) {
      if (!previewThemeId) throw previewError;
      url.searchParams.delete('preview_theme_id');
      return requestIndex(url);
    }
  };

  const indexBaseUrl = () => root()?.dataset.searchIndexUrl || '/collections/all?view=pw-search-index';

  const cacheKey = () => {
    const url = new URL(indexBaseUrl(), window.location.origin);
    url.searchParams.delete('preview_theme_id');
    url.searchParams.delete('_pw_sku_refresh');
    return `pw-sku-index:${INDEX_CACHE_VERSION}:${url.pathname}${url.search}`;
  };

  const readCachedCatalog = () => {
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey()) || 'null');
      if (!cached || !Array.isArray(cached.products)) return null;
      if (Date.now() - Number(cached.cachedAt || 0) > INDEX_CACHE_TTL) return null;
      return cached.products;
    } catch (_) {
      return null;
    }
  };

  const writeCachedCatalog = (products) => {
    try {
      sessionStorage.setItem(cacheKey(), JSON.stringify({ cachedAt: Date.now(), products }));
    } catch (_) {
      // Zoeken blijft werken zonder sessionStorage.
    }
  };

  const loadCatalog = async () => {
    if (catalogPromise) return catalogPromise;

    const cached = readCachedCatalog();
    if (cached) {
      catalogPromise = Promise.resolve(cached);
      return catalogPromise;
    }

    catalogPromise = (async () => {
      const base = indexBaseUrl();
      const first = await fetchIndexPage(base, 1);
      const pages = Math.max(1, Number(first.pages || 1));
      const requests = [];
      for (let page = 2; page <= pages; page += 1) requests.push(fetchIndexPage(base, page));
      const products = [first, ...(await Promise.all(requests))].flatMap((payload) => payload.products || []);
      writeCachedCatalog(products);
      return products;
    })().catch((error) => {
      catalogPromise = null;
      throw error;
    });

    return catalogPromise;
  };

  const scoreValues = (values, needle, offset = 0) => {
    let best = Infinity;
    for (const value of values || []) {
      const haystack = normalizeId(value);
      if (!haystack) continue;
      if (haystack === needle) best = Math.min(best, offset);
      else if (haystack.startsWith(needle)) best = Math.min(best, offset + 100 + haystack.length - needle.length);
      else {
        const position = haystack.indexOf(needle);
        if (position !== -1) best = Math.min(best, offset + 300 + position);
      }
    }
    return best;
  };

  const catalogMatches = (products, query) => {
    const needle = normalizeId(query);
    if (!needle) return [];

    return products
      .filter((product) => !isHidden(product) && productUrl(product))
      .map((product) => {
        const skuScore = scoreValues(product.skus || [], needle, 0);
        const barcodeScore = scoreValues(product.barcodes || [], needle, 500);
        const titleScore = scoreValues([product.title], needle, 2000);
        const score = Math.min(skuScore, barcodeScore, titleScore);
        return Number.isFinite(score) ? { ...product, _pwSkuScore: score } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a._pwSkuScore - b._pwSkuScore || String(a.title || '').localeCompare(String(b.title || ''), 'nl'));
  };

  const mergeProducts = (groups, hiddenPaths = new Set()) => {
    const seen = new Set();
    const merged = [];

    for (const product of groups.flat()) {
      if (!product || isHidden(product)) continue;
      const path = productPath(product);
      if (!path || hiddenPaths.has(path) || seen.has(path)) continue;
      seen.add(path);
      merged.push(product);
      if (merged.length >= RESULT_LIMIT) break;
    }
    return merged;
  };

  const fetchStandardCard = async (product) => {
    const rawUrl = productUrl(product);
    if (!rawUrl) return null;

    const url = new URL(rawUrl, window.location.origin);
    url.searchParams.set('view', 'pw-search-card');
    const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);

    const requestCard = async (candidate) => {
      const response = await fetch(candidate, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'text/html' }
      });
      if (!response.ok) throw new Error(`Productkaart kon niet worden geladen (${response.status}).`);
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const card = parsed.querySelector('.pw-product-card');
      if (!card) throw new Error('Productkaart ontbreekt in response.');
      return document.importNode(card, true);
    };

    try {
      return await requestCard(url);
    } catch (previewError) {
      if (!previewThemeId) return null;
      url.searchParams.delete('preview_theme_id');
      try {
        return await requestCard(url);
      } catch (_) {
        return null;
      }
    }
  };

  const resultsGrid = (currentSection) => currentSection?.querySelector('[data-results-grid]');

  const ensureGrid = (currentSection) => {
    const host = resultsGrid(currentSection);
    if (!host) return null;

    let grid = host.querySelector('.pw-search-mixed-grid');
    if (grid) return grid;

    const group = document.createElement('div');
    group.className = 'pw-search-results-group';
    grid = document.createElement('div');
    grid.className = 'pw-search-mixed-grid pw-product-grid';
    group.append(grid);
    host.replaceChildren(group);
    return grid;
  };

  const currentProductPaths = (currentSection) => new Set(
    [...(resultsGrid(currentSection)?.querySelectorAll('a[href*="/products/"]') || [])]
      .map((link) => {
        try {
          return new URL(link.href, window.location.origin).pathname.replace(/\/$/, '').toLowerCase();
        } catch (_) {
          return '';
        }
      })
      .filter(Boolean)
  );

  const updateCount = (currentSection) => {
    const host = resultsGrid(currentSection);
    if (!host) return;
    const count = host.querySelectorAll('.pw-product-card, .pw-search-content-card').length;
    if (!count) return;
    const label = currentSection.querySelector('.pw-results-toolbar__count');
    if (label) label.textContent = `${count} ${count === 1 ? 'resultaat' : 'resultaten'}`;
    currentSection.dataset.resultCount = String(count);
  };

  const run = async () => {
    const currentSection = section();
    if (!currentSection) return;

    const params = new URLSearchParams(window.location.search);
    const query = String(params.get('q') || '').trim();
    if (query.length < MIN_QUERY) return;
    if (params.has('sort_by') || params.has('page') || [...params.keys()].some((key) => key.startsWith('filter.'))) return;

    const serial = ++runSerial;
    let predictive = [];
    let catalog = [];

    try {
      const sets = await Promise.all(queryCandidates(query).map((candidate) => fetchPredictive(candidate)));
      predictive = mergeProducts(sets);
    } catch (_) {
      predictive = [];
    }

    if (serial !== runSerial || currentSection !== section()) return;

    const useIndex = isCompact(query) && normalizeId(query).length >= INDEX_MIN;
    if (useIndex) {
      try {
        catalog = await loadCatalog();
      } catch (_) {
        catalog = [];
      }
    }

    if (serial !== runSerial || currentSection !== section()) return;

    const hiddenPaths = new Set(catalog.filter((product) => isHidden(product)).map(productPath).filter(Boolean));
    const indexed = useIndex ? catalogMatches(catalog, query) : [];
    const matches = mergeProducts([indexed, predictive], hiddenPaths);
    if (!matches.length) return;

    const existing = currentProductPaths(currentSection);
    const missing = matches.filter((product) => {
      const path = productPath(product);
      return path && !existing.has(path);
    });
    if (!missing.length) return;

    const cards = (await Promise.all(missing.map(fetchStandardCard))).filter(Boolean);
    if (serial !== runSerial || currentSection !== section() || !cards.length) return;

    const grid = ensureGrid(currentSection);
    if (!grid) return;
    cards.forEach((card) => grid.append(card));
    updateCount(currentSection);
  };

  const schedule = () => window.setTimeout(run, 0);

  document.addEventListener('paulissen:search:rendered', schedule);
  window.addEventListener('popstate', schedule);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true });
  } else {
    schedule();
  }
})();
